
import React, { useEffect } from 'react';
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Candidate } from '@/components/candidates/types';
import { BasicInfo } from '@/components/candidates/profile/BasicInfo';
import { ContactInfo } from '@/components/candidates/profile/ContactInfo';
import { Skills } from '@/components/candidates/profile/Skills';
import { Notes } from '@/components/candidates/profile/Notes';
import { LoadingState } from '@/components/error-handling/LoadingState';
import { ErrorState } from '@/components/error-handling/ErrorState';
import { getStatusColor } from '@/components/candidates/utils/statusUtils';
import { JobMatchingSection } from '@/components/candidates/matching/JobMatchingSection';
import { ResumeSection } from '@/components/candidates/resume/ResumeSection';
import { EnhancedInteractionHistory } from '@/components/candidates/interactions/EnhancedInteractionHistory';
import { CandidateInsights } from '@/components/candidates/insights/CandidateInsights';
import { User, Briefcase, FileText, MessageSquare, TrendingUp, UserCheck } from 'lucide-react';
import { convertDatabaseCandidateToCandidate } from '@/utils/candidateDataUtils';
import { EmploymentAndPreferences } from '@/components/candidates/profile/EmploymentAndPreferences';

const fetchCandidate = async (candidateId: string): Promise<Candidate | null> => {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('id', candidateId)
    .single();
  if (error) throw error;
  return data ? convertDatabaseCandidateToCandidate(data) : null;
};

const CandidateDetail = () => {
  const { id } = useParams();
  
  const { data: candidate, isLoading, error, refetch } = useQuery<Candidate | null, Error>({
    queryKey: ['candidate', id],
    queryFn: () => fetchCandidate(id!),
    enabled: !!id,
  });

  const fullName = candidate ? `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() : '';
  const initials = candidate ? `${candidate.first_name?.[0] || ''}${candidate.last_name?.[0] || ''}`.toUpperCase() : '';

  if (isLoading) {
    return <LoadingState message="Loading candidate profile..." />;
  }

  if (error) {
    return <ErrorState title="Error loading profile" description={error.message} onRetry={refetch} />;
  }

  if (!candidate) {
    return <ErrorState title="Candidate not found" description="No candidate data available for this ID." />;
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      {/* Header Card */}
      <Card className="border-none shadow-none">
        <CardHeader className="flex flex-row items-center gap-4 p-4 md:p-6">
          <Avatar className="h-16 w-16 md:h-20 md:w-20">
            <AvatarImage src={candidate.resume_url || undefined} alt={fullName} />
            <AvatarFallback>{initials || 'N/A'}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <CardTitle className="text-xl md:text-2xl font-semibold">{fullName || 'Unnamed Candidate'}</CardTitle>
            <div className="flex items-center gap-2 mt-1">
              {candidate.status && (
                <Badge className={`text-xs md:text-sm ${getStatusColor(candidate.status)}`}>
                  {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
                </Badge>
              )}
              {candidate.experience_years && (
                <Badge variant="outline" className="text-xs md:text-sm">
                  {candidate.experience_years} years exp.
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabbed Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="employment" className="flex items-center gap-1">
            <UserCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Employment</span>
          </TabsTrigger>
          <TabsTrigger value="matching" className="flex items-center gap-1">
            <Briefcase className="h-4 w-4" />
            <span className="hidden sm:inline">Job Matching</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Documents</span>
          </TabsTrigger>
          <TabsTrigger value="interactions" className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Interactions</span>
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Insights</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <BasicInfo candidate={candidate} />
            <ContactInfo candidate={candidate} />
          </div>
          <Skills candidate={candidate} />
          <Notes candidate={candidate} />
        </TabsContent>

        <TabsContent value="employment" className="mt-6">
          <EmploymentAndPreferences candidate={candidate} />
        </TabsContent>

        <TabsContent value="matching" className="mt-6">
          <JobMatchingSection candidate={candidate} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <ResumeSection candidate={candidate} />
        </TabsContent>

        <TabsContent value="interactions" className="mt-6">
          <EnhancedInteractionHistory candidateId={candidate.id} />
        </TabsContent>

        <TabsContent value="insights" className="mt-6">
          <CandidateInsights candidate={candidate} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CandidateDetail;
